type Row = Record<string, unknown>;
const row = (value: unknown): Row => value && typeof value === "object" && !Array.isArray(value) ? value as Row : {};
const rows = (value: unknown): Row[] => Array.isArray(value) ? value.map(row) : [];
const text = (value: unknown): string => typeof value === "string" || typeof value === "number" ? String(value).trim() : "";
function day(value: unknown): Date | null {
  const valueText = text(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(valueText)) return null;
  const parsed = new Date(valueText + "T12:00:00Z");
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === valueText ? parsed : null;
}
const locale = (language: string) => language === "en" ? "en-GB" : language === "es" ? "es-ES" : "it-IT";
export function shareDate(value: unknown, language: string): string {
  const date = day(value);
  return date ? new Intl.DateTimeFormat(locale(language), { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(date) : "";
}
export function shareDateRange(start: unknown, end: unknown, language: string): string {
  const a = day(start), b = day(end);
  if (!a || !b || b < a) return [shareDate(start, language), shareDate(end, language)].filter(Boolean).join(" · ");
  return new Intl.DateTimeFormat(locale(language), { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }).formatRange(a, b);
}
export function checklistGroups(value: unknown): Array<{ id: string; name: string; items: Row[] }> {
  const data = row(value), items = rows(data.items).filter(item => text(item.title || item.name || item.text));
  const used = new Set<Row>();
  const groups = rows(data.lists).flatMap(list => {
    const id = text(list.id);
    if (!id) return [];
    const own = items.filter(item => text(item.checklistId || item.listId) === id);
    if (!own.length) return [];
    own.forEach(item => used.add(item));
    return [{ id, name: text(list.name || list.title), items: own }];
  });
  // Legacy projection lost checklistId. Retain genuine shared items without inventing list membership.
  const unassigned = items.filter(item => !used.has(item));
  if (unassigned.length) groups.push({ id: "shared-unassigned", name: "", items: unassigned });
  return groups;
}
export function usefulWeatherRows(value: unknown): Row[] {
  return rows(value).filter(item => !!text(item.conditions) || [item.temperature, item.minTemp, item.maxTemp, item.minTemperature, item.maxTemperature].some(value => /^[-+]?\d+(?:[.,]\d+)?(?:\s*°?\s*[CF])?$/i.test(text(value))));
}
export function weatherTemperature(value: unknown): string {
  const valueText = text(value);
  if (!/^[-+]?\d+(?:[.,]\d+)?(?:\s*°?\s*[CF])?$/i.test(valueText)) return "";
  return /[CF]$/i.test(valueText) ? valueText : `${valueText} °C`;
}
export function locationLine(item: Row, language: string): string {
  const name = text(item.name || item.title), city = text(item.city), address = text(item.address);
  const fold = (value: string) => value.toLocaleLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
  if (city && fold(name) === fold(city)) {
    const country = address.match(/,\s*([A-Z]{2})$/)?.[1] || text(item.countryCode);
    if (country && (!address || fold(address.replace(/,\s*[A-Z]{2}$/, "")) === fold(city))) {
      try { return new Intl.DisplayNames([locale(language)], { type: "region" }).of(country) || country; } catch { return country; }
    }
  }
  if (city && address && fold(address).includes(fold(city))) return address;
  return [...new Set([city, address].filter(Boolean))].join(" · ");
}
export function descriptionParts(value: unknown): { body: string; details: string; proposed: boolean; attributions: Array<{ label: string; url: string }> } {
  const original = text(value), attributions: Array<{ label: string; url: string }> = [];
  if (/OpenStreetMap|openstreetmap\.org/i.test(original)) attributions.push({ label: "© OpenStreetMap contributors · ODbL", url: "https://www.openstreetmap.org/copyright" });
  if (/GeoNames|geonames\.org/i.test(original)) attributions.push({ label: "GeoNames · CC BY 4.0", url: "https://www.geonames.org/" });
  const technical = original.search(/Coordinate della località|Localizzazione\s*:|Dati cartografici|Riscontro\s+\d/i);
  const proposed = /(?:Struttura reale inserita nel percorso come opzione di alloggio|Locale reale inserito nel percorso)[^.!]*nessuna prenotazione registrata/i.test(original);
  let body = technical >= 0 ? original.slice(0, technical).trim() : original;
  if (proposed) body = body.replace(/(?:Struttura reale inserita nel percorso come opzione di alloggio|Locale reale inserito nel percorso)[^.!]*nessuna prenotazione registrata\.?/i, "").replace(/Disponibilità e condizioni dipendono dalla struttura\.?/i, "").trim();
  return { body, details: technical >= 0 || proposed ? original : "", proposed, attributions };
}
